import React, { createContext, useContext, useReducer } from "react";
import { createEvent, updateEvent } from "../../../api/event";
import { useToast } from "../../../context/ToastContext";

const WizardContext = createContext();

const initialState = {
  isOpen: false,
  step: 1,
  totalSteps: 3, // Will be 3 for auth'd, 4 for unauth'd
  isAuthenticated: false,
  isDirectQuote: false,
  professionalDetails: null,
  isEditMode: false,
  eventId: null,
  initialData: null,
  
  // Data
  formData: {
    // Basic Info
    eventName: "",
    eventType: "",
    locations: [],
    isMultiDay: false,
    startDate: "",
    endDate: "",
    hiringRole: "", // 'planner', 'performer', 'crew'
    assignedTo: null, // For direct quotes
    
    // Role Specific - Planner
    services: [], // Categories
    guests: 10,
    
    // Role Specific - Performer / Crew
    durationHours: 1,
    amenities: {
      food: false,
      travel: false,
      accommodation: false,
      outfit: false // Crew only
    },
    
    // Budget & Extra
    budget: { amount: "", currency: "INR" },
    additionalInfo: "",

    // Auth specific (Unauthenticated flow)
    contactDetails: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
      termsAccepted: true,
      accuracyConfirmed: true
    },
    
    // Login flow inside wizard
    loginDetails: {
      email: "",
      password: ""
    }
  },
  isSuccess: false,
  isLoggingIn: false,
};

function wizardReducer(state, action) {
  switch (action.type) {
    case "OPEN":
      return { 
        ...state, 
        isOpen: true, 
        isAuthenticated: action.payload.isAuthenticated,
        isDirectQuote: action.payload.isDirectQuote || false,
        professionalDetails: action.payload.professionalDetails || null,
        isEditMode: action.payload.isEditMode || false,
        eventId: action.payload.eventId || null,
        initialData: action.payload.initialData || null,
        totalSteps: action.payload.isAuthenticated ? 3 : 4,
        formData: {
          ...state.formData,
          ...action.payload.prefill,
          locations: action.payload.prefill?.locations
            ? (Array.isArray(action.payload.prefill.locations) ? action.payload.prefill.locations : [action.payload.prefill.locations])
            : (action.payload.prefill?.location ? (Array.isArray(action.payload.prefill.location) ? action.payload.prefill.location : [action.payload.prefill.location]) : (state.formData.locations || []))
        }
      };
    case "CLOSE":
      return { ...initialState }; // Reset on close
    case "SET_STEP":
      return { ...state, step: action.payload };
    case "NEXT_STEP":
      return { ...state, step: Math.min(state.step + 1, state.totalSteps) };
    case "PREV_STEP":
      return { ...state, step: Math.max(state.step - 1, 1) };
    case "UPDATE_FIELD":
      return {
        ...state,
        formData: { ...state.formData, ...action.payload }
      };
    case "UPDATE_CONTACT":
      return {
        ...state,
        formData: {
          ...state.formData,
          contactDetails: { ...state.formData.contactDetails, ...action.payload }
        }
      };
    case "UPDATE_LOGIN":
      return {
        ...state,
        formData: {
          ...state.formData,
          loginDetails: { ...state.formData.loginDetails, ...action.payload }
        }
      };
    case "SET_AUTH":
      return {
        ...state,
        isAuthenticated: true,
        isLoggingIn: false,
        totalSteps: state.step > 3 ? state.totalSteps : 3 // Keep at 4 if they are already on step 4 to prevent UI glitches
      };
    case "SET_LOGGING_IN":
      return {
        ...state,
        isLoggingIn: action.payload
      };
    default:
      return state;
  }
}

export function EventWizardProvider({ children }) {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const { showError } = useToast();

  const openWizard = (config = {}) => {
    const token = localStorage.getItem("authToken");
    const isLoggedIn = !!token && token !== "null" && token !== "undefined";
    dispatch({ 
      type: "OPEN", 
      payload: { 
        isAuthenticated: isLoggedIn, 
        ...config 
      } 
    });
  };

  const closeWizard = () => {
    const wasSuccess = state.isSuccess;
    dispatch({ type: "CLOSE" });
    if (wasSuccess) {
      window.location.reload();
    }
  };
  const setStep = (step) => dispatch({ type: "SET_STEP", payload: step });
  const setSuccess = (value) => dispatch({ type: "UPDATE_FIELD", payload: { isSuccess: value } });
  const nextStep = () => dispatch({ type: "NEXT_STEP" });
  const prevStep = () => dispatch({ type: "PREV_STEP" });
  
  const updateField = (fields) => dispatch({ type: "UPDATE_FIELD", payload: fields });
  const updateContact = (fields) => dispatch({ type: "UPDATE_CONTACT", payload: fields });
  const updateLogin = (fields) => dispatch({ type: "UPDATE_LOGIN", payload: fields });
  const setAuthenticated = () => dispatch({ type: "SET_AUTH" });
  const setIsLoggingIn = (value) => dispatch({ type: "SET_LOGGING_IN", payload: value });

  React.useEffect(() => {
    const handleOpen = (e) => {
      const config = e.detail || {};
      openWizard(config);
    };
    window.addEventListener("openEventWizard", handleOpen);
    return () => window.removeEventListener("openEventWizard", handleOpen);
  }, []);

  const submitEvent = async () => {
    try {
      const { formData } = state;
      const payload = {
        eventName: formData.eventName,
        eventType: formData.eventType,
        eventTypeModel: formData.eventTypeModel || "Category",
        roleType: formData.hiringRole,
        locations: (formData.locations || []).map(loc => typeof loc === 'object' && loc !== null ? (loc.id || loc._id || loc.name) : loc),
        startDate: formData.startDate,
        endDate: (formData.isMultiDay === true || formData.isMultiDay === 'true') ? formData.endDate : null,
        budget: {
          amount: Number(formData.budget.amount),
          currency: formData.budget.currency || "INR",
        },
        additionalInfo: formData.additionalInfo,
        assignedTo: formData.assignedTo || null,
        servicesRequired: formData.services || [],
        subServicesRequired: formData.subServices || [],
      };

      if (formData.hiringRole === "planner") {
        payload.guests = formData.guests;
        // Optionally map formData.services to servicesRequired if needed, though they are string labels for now
      } else if (formData.hiringRole === "performer") {
        payload.durationHours = Number(formData.durationHours) || 1;
        payload.performerAmenities = {
          food: !!formData.amenities.food,
          travel: !!formData.amenities.travel,
          accommodation: !!formData.amenities.accommodation
        };
      } else if (formData.hiringRole === "crew") {
        payload.durationHours = Number(formData.durationHours) || 1;
        payload.crewAmenities = {
          food: !!formData.amenities.food,
          travel: !!formData.amenities.travel,
          accommodation: !!formData.amenities.accommodation,
          outfit: !!formData.amenities.outfit
        };
      }

      let createdEventId = null;
      if (state.isEditMode) {
        await updateEvent(state.eventId, payload);
        createdEventId = state.eventId;
      } else {
        const res = await createEvent(payload);
        createdEventId = res?.data?._id || res?.data?.id || res?._id || res?.eventId || null;
      }
      return { success: true, eventId: createdEventId };
    } catch (error) {
      showError(error.message || "Failed to submit event");
      return false;
    }
  };

  return (
    <WizardContext.Provider
      value={{
        ...state,
        openWizard,
        closeWizard,
        setStep,
        nextStep,
        prevStep,
        updateField,
        updateContact,
        updateLogin,
        setAuthenticated,
        setIsLoggingIn,
        submitEvent,
        setSuccess
      }}
    >
      {children}
    </WizardContext.Provider>
  );
}

export function useWizardContext() {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error("useWizardContext must be used within an EventWizardProvider");
  }
  return context;
}
